import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { socket, serverUrl } from '../socket.js';
import { useI18n } from '../useI18n.js';
import { GAME_MODES, MODE_SOURCES } from '../gameModes.js';
import { AuthorCurationPanel } from './AuthorCurationPanel';
import { subscribeAuth, getAuth } from '../auth';
import { isMashupAllowed, typologyOf } from '../mashupWhitelist';
// Context interne simple (faute d’un store global) pour propager auth vers le badge
const discordAuthListeners = new Set();
let _discordAuthState = null;
function setGlobalDiscordAuth(v){
  _discordAuthState = v;
  discordAuthListeners.forEach(l=>{ try { l(v); } catch{ /* noop */ } });
}
function useDiscordAuthGlobal(){
  const [v,setV] = useState(_discordAuthState);
  useEffect(()=>{ const l = (nv)=>setV(nv); discordAuthListeners.add(l); return ()=>discordAuthListeners.delete(l); },[]);
  return v;
}

function DiscordUserBadge(){
  const auth = useDiscordAuthGlobal();
  if(!auth || !auth.user) return null;
  const u = auth.user;
  const avatarUrl = u.avatar ? `https://cdn.discordapp.com/avatars/${u.id}/${u.avatar}.png?size=64` : `https://cdn.discordapp.com/embed/avatars/${(parseInt(u.discriminator||'0',10)%5)}.png`;
  return (
    <div style={{ marginLeft: 'auto', display:'flex', alignItems:'center', gap:'.4rem', position:'absolute', right:0, top:0 }}>
      <img src={avatarUrl} alt="pfp" style={{ width:32, height:32, borderRadius: '50%', objectFit:'cover', boxShadow:'0 0 0 2px #0002' }} />
      <span style={{ fontSize:'.65rem', fontWeight:600 }}>{u.global_name || `${u.username}${u.discriminator?('#'+u.discriminator):''}`}</span>
    </div>
  );
}

// helper pour formater 65 → "01:05"
function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}`;
}

export function GameHeader({
  roundNumber,
  phase,
  timeLeft,
  isChef,
  gameStarted,
  roomParams,
  setRoomParams,
  onUploadCsv,
  onDiscordScrape,
  datasetReady,
  datasetAuthors,
  detectedNames,
  keptAuthors,
  onKeptAuthors,
  maxAuthors,
  datasetPeriod,
  sources,
  onRemoveSource,
  uploading,
  uploadProgress,
  onSelectMode,
  modeAccess,
}) {
  const { t } = useI18n();
  const phaseLabel = phase === 'thinking' ? t('phase_thinking')
    : phase === 'result' ? t('phase_result')
    : phase === 'transition' ? t('phase_transition')
    : phase === 'ended' ? t('phase_ended')
    : phase || '';
  // Barre compacte affichée en haut de l'écran sur mobile
  const mobileTopbar = (
    <div className="mobile-topbar">
      <div className="mobile-topbar-content">
  {gameStarted && <span className="mtb-round">{t('round')} {roundNumber}</span>}
  {gameStarted && <span className="mtb-phase">{phaseLabel}</span>}
        {gameStarted && timeLeft > 0 && (
          <span className="mtb-timer">{formatTime(timeLeft)}</span>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {mobileTopbar}
      <div className="game-header-row" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', position: 'relative' }}>
        {gameStarted && <div>{t('round')}: {roundNumber}</div>}
        {gameStarted && (
        <div>
          {t('phase')}: {phaseLabel}
          {timeLeft > 0 && (
            <span style={{ marginLeft: '0.5rem', fontWeight: 'bold' }}>
              {formatTime(timeLeft)}
            </span>
          )}
        </div>
        )}
        {/* Le badge utilisateur (login + ELO) est global (LoginBadge, monte dans main.jsx) */}
      </div>
      {isChef && !gameStarted && (
        <ModeAndConfigTabs
          roomParams={roomParams}
          setRoomParams={setRoomParams}
          t={t}
          onUploadCsv={onUploadCsv}
          onDiscordScrape={onDiscordScrape}
          datasetReady={datasetReady}
          datasetAuthors={datasetAuthors}
          detectedNames={detectedNames}
          keptAuthors={keptAuthors}
          onKeptAuthors={onKeptAuthors}
          maxAuthors={maxAuthors}
          datasetPeriod={datasetPeriod}
          sources={sources}
          onRemoveSource={onRemoveSource}
          uploading={uploading}
          uploadProgress={uploadProgress}
          onSelectMode={onSelectMode}
          modeAccess={modeAccess}
        />
      )}
    </div>
  );
}

// --- Discord OAuth helpers (front) ---
function startDiscordOAuth(setDiscordAuth, setOauthError) {
  setOauthError(null);
  const w = 550, h = 750;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  // Déterminer l'origine du serveur (dev: client sur 5173, serveur sur 3000)
  const serverOrigin = serverUrl;
  const loginUrl = serverOrigin.replace(/\/$/, '') + '/auth/discord/login';
  const popup = window.open(loginUrl, 'discord_oauth', `width=${w},height=${h},left=${left},top=${top}`);
  if (!popup) {
    setOauthError('Popup blocked');
    return;
  }
  function handler(ev) {
    if (!ev.data || ev.data.source !== 'discord-oauth') return;
    const { payload } = ev.data;
    if (payload.ok) {
      setDiscordAuth({
        user: payload.user,
        guilds: payload.guilds || [],
        selectedGuildIds: new Set(),
        tokenId: payload.tokenId
      });
      setOauthError(null);
    } else {
      setOauthError(payload.error || 'OAuth error');
    }
    window.removeEventListener('message', handler);
  }
  window.addEventListener('message', handler);
}

function GuildsModal({ open, onClose, auth, setAuth, t }) {
  const overlayRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const content = (
    <div ref={overlayRef} className="import-modal-overlay" onMouseDown={e => { if (e.target === overlayRef.current) onClose(); }}>
      <div className="import-modal card" role="dialog" aria-modal="true" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="import-modal-header" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{t('selectGuilds')}</h3>
          <button className="btn" onClick={onClose}>{t('close')}</button>
        </div>
        <div className="import-modal-body" style={{ maxHeight: '55vh', overflow: 'auto' }}>
          {!auth.guilds?.length && <div style={{ fontSize: '.7rem' }}>{t('noGuildsFound')}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
            {auth.guilds?.map(g => {
              const selected = auth.selectedGuildIds?.has(g.id);
              return (
                <button key={g.id} type="button" className="channel-chip" onClick={() => {
                  setAuth(prev => {
                    const next = new Set(prev.selectedGuildIds);
                    if (next.has(g.id)) next.delete(g.id); else next.add(g.id);
                    return { ...prev, selectedGuildIds: next };
                  });
                }} style={{
                  padding: '0.35rem 0.6rem',
                  fontSize: '0.65rem',
                  border: '2px solid ' + (selected ? 'var(--color-primary)' : 'var(--border-color)'),
                  background: selected ? 'rgba(86,99,247,0.16)' : '#fff',
                  cursor: 'pointer',
                  boxShadow: 'var(--shadow-pixel)',
                  fontWeight: 600,
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start'
                }}>
                  <span>{g.name}</span>
                  {g.owner && <span style={{ fontSize: '.55rem', opacity: .6 }}>{t('guildOwner')}</span>}
                </button>
              );
            })}
          </div>
        </div>
        <div className="import-modal-footer" style={{ justifyContent: 'center' }}>
          <button className="btn" onClick={onClose}>{t('confirm')}</button>
        </div>
      </div>
    </div>
  );
  return createPortal(content, document.body);
}

// Messages d'erreur du scrape serveur
const SCRAPE_ERRORS = {
  no_channels: 'Aucun salon selectionne.',
  no_authors: 'Aucun auteur selectionne.',
  no_messages: 'Aucun message trouve pour cette selection.',
  no_valid_authors: 'Aucun auteur exploitable dans cette selection.',
  missing_bot_token: 'Bot non configure cote serveur.',
  forbidden_not_chef: 'Seul le chef peut importer.',
  game_already_started: 'La partie a deja demarre.',
  room_not_found: 'Salon introuvable.',
};
function scrapeErrorLabel(code) {
  return SCRAPE_ERRORS[code] || `Erreur: ${code || 'inconnue'}`;
}

// Nouvelle modal combinée Guilds + Channels
function GuildsChannelsModal({ open, onClose, auth, setAuth, onScrape, t }) {
  const overlayRef = useRef(null);
  // Guildes deja tentees (succes OU echec) : evite le refetch en boucle sur erreur reseau
  const fetchedGuildsRef = useRef(new Set());
  const [scraping, setScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState(null);
  const initialModalState = auth.modalState || {};
  const [activeGuildId, setActiveGuildId] = useState(initialModalState.activeGuildId || auth.guilds?.[0]?.id || null);
  const [channelsMap, setChannelsMap] = useState(initialModalState.channelsMap || {}); // guildId -> [{id,name}]
  const [loadingGuild, setLoadingGuild] = useState(null);
  const [errorGuild, setErrorGuild] = useState(null);
  const [guildAccessIssues, setGuildAccessIssues] = useState(initialModalState.guildAccessIssues || {}); // guildId -> '403' | 'empty'
  const MAX_CHANNELS = 15;
  const [step, setStep] = useState(initialModalState.step || 1); // 1=channels, 2=authors
  const [loadingAuthors, setLoadingAuthors] = useState(false);
  const [authors, setAuthors] = useState(initialModalState.authors || []); // {id, username, global_name, discriminator, avatar, count}
  const [authorSelection, setAuthorSelection] = useState(new Set(initialModalState.authorSelection || []));
  const MIN_AUTHORS = 2;
  const MAX_AUTHORS = 25;

  // Compte total sélectionné
  const totalSelected = Array.from(auth.channelSelections?.values() || []).reduce((acc, set) => acc + set.size, 0);

  // Persistance de l'état de la modal (pour ré-ouverture) – on mémorise seulement quand ouverte
  useEffect(() => {
    if (!open) return; // ne pas écraser quand fermée
    // Conversion du Set d'auteurs en tableau pour sérialisation légère
    const modalState = {
      activeGuildId,
      channelsMap,
      guildAccessIssues,
      step,
      authors,
      authorSelection: Array.from(authorSelection)
    };
    setAuth(prev => ({ ...prev, modalState }));
  }, [open, activeGuildId, channelsMap, guildAccessIssues, step, authors, authorSelection, setAuth]);

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    const gid = activeGuildId;
    // Ne fetch qu'une fois par guilde (succes OU echec) : sinon refetch en boucle sur erreur.
    if (!gid || channelsMap[gid] || fetchedGuildsRef.current.has(gid)) return;
    fetchedGuildsRef.current.add(gid);
    let cancelled = false;
    (async () => {
      setLoadingGuild(gid);
      setErrorGuild(null);
      try {
        const url = serverUrl.replace(/\/$/, '') + `/api/discord/guilds/${gid}/channels?readableOnly=1`;
        const r = await fetch(url);
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (data && data.ok) {
          setChannelsMap(prev => ({ ...prev, [gid]: data.channels }));
          setGuildAccessIssues(prev => {
            const copy = { ...prev };
            if (data.channels.length === 0) {
              copy[gid] = 'empty';
            } else if (copy[gid]) {
              delete copy[gid];
            }
            return copy;
          });
        } else if (data?.status === 403 || data?.detail?.includes?.('Missing Access')) {
          setGuildAccessIssues(prev => ({ ...prev, [gid]: '403' }));
        } else {
          setErrorGuild(data?.error || 'error');
        }
      } catch {
        if (!cancelled) setErrorGuild('network');
      } finally {
        if (!cancelled) setLoadingGuild(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeGuildId, channelsMap]);

  // Force le rafraîchissement (retente le fetch pour cette guilde)
  function refreshGuild(gid) {
    if (!gid) return;
    fetchedGuildsRef.current.delete(gid);
    setErrorGuild(null);
    setChannelsMap(prev => { const copy = { ...prev }; delete copy[gid]; return copy; });
    setGuildAccessIssues(prev => { const copy = { ...prev }; delete copy[gid]; return copy; });
  }

  if (!open) return null;

  const guilds = auth.guilds || [];
  const currentChannels = channelsMap[activeGuildId] || [];

  const toggleChannel = (gid, cid) => {
    setAuth(prev => {
      const channelSelections = new Map(prev.channelSelections || []);
      const setForGuild = new Set(channelSelections.get(gid) || []);
      if (setForGuild.has(cid)) {
        setForGuild.delete(cid);
      } else {
        // Calcule total actuel (évite dépendance à totalSelected potentiellement désynchronisé)
        const currentTotal = Array.from(channelSelections.values()).reduce((acc, s) => acc + s.size, 0);
        if (currentTotal >= MAX_CHANNELS) return prev;
        setForGuild.add(cid);
      }
      if (setForGuild.size === 0) channelSelections.delete(gid); else channelSelections.set(gid, setForGuild);
      return { ...prev, channelSelections };
    });
  };

  const content = (
    <div ref={overlayRef} className="import-modal-overlay" onMouseDown={e => { if (e.target === overlayRef.current) onClose(); }}>
      <div className="import-modal card" role="dialog" aria-modal="true" onMouseDown={e => e.stopPropagation()} style={{ width:'min(960px,95vw)', maxWidth:960, padding:0, display:'flex', flexDirection:'column' }}>
        <div className="import-modal-header" style={{ justifyContent:'space-between', padding:'0.75rem 1rem', borderBottom:'1px solid var(--border-color)' }}>
          <div>
            <h3 style={{ margin:0, fontSize:'0.95rem' }}>{step === 1 ? '1/2 - Serveur & salons' : '2/2 - Joueurs'}</h3>
            <div style={{ fontSize:'.62rem', opacity:.7, marginTop:2 }}>
              {step === 1 ? 'Choisis un serveur (colonne de gauche), puis coche les salons a analyser.' : 'Coche les joueurs a inclure dans cette source.'}
            </div>
          </div>
          <button className="btn" onClick={onClose}>{t('close')}</button>
        </div>
        {step === 1 && (
        <div style={{ display:'flex', minHeight:420, maxHeight:'60vh' }}>
          {/* Rail vertical Discord-like */}
          <div style={{ width:70, background:'#f5f5f8', borderRight:'1px solid var(--border-color)', display:'flex', flexDirection:'column', alignItems:'center', padding:'0.6rem 0', gap:'.65rem', overflowY:'auto' }}>
            {(!guilds || guilds.length === 0) && (
              <div style={{ width:'100%', padding:'0 .4rem', textAlign:'center', fontSize:'.55rem', opacity:.6 }}>{t('noAccessibleServers') || 'No accessible servers'}</div>
            )}
            {guilds.map(g => {
              const selected = g.id === activeGuildId;
              const iconUrl = g.icon ? `https://cdn.discordapp.com/icons/${g.id}/${g.icon}.png?size=64` : null;
              const tooltip = g.name;
              return (
                <div key={g.id} style={{ position:'relative' }}>
                  <button type="button" title={tooltip} onClick={() => setActiveGuildId(g.id)} style={{
                    width:48, height:48,
                    // Rayon fixe pour éviter tout déplacement du point de clic (suppression jitter)
                    borderRadius:24,
                    background: iconUrl ? `center/cover no-repeat url(${iconUrl})` : '#686b78',
                    // Utilise outline (non compté dans la boîte) + boxShadow pour signaler la sélection sans modifier la géométrie
                    border:'2px solid transparent',
                    outline: selected ? '2px solid var(--color-primary)' : '2px solid transparent',
                    outlineOffset:0,
                    cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'.65rem', fontWeight:700,
                    boxShadow: selected ? '0 0 0 3px rgba(86,99,247,0.3)' : '0 0 0 2px #00000008',
                    transition:'none'
                  }}>{!iconUrl && g.name.slice(0,2).toUpperCase()}</button>
                </div>
              );
            })}
          </div>
          {/* Liste des channels du serveur actif */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', padding:'0.75rem 1rem', overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'.5rem', gap:'.75rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'.5rem' }}>
                <div style={{ fontSize:'.75rem', fontWeight:700 }}>
                  {guilds.find(g=>g.id===activeGuildId)?.name || t('selectGuilds')}
                </div>
                {activeGuildId && (
                  <button type="button" className="btn" style={{ padding:'.15rem .5rem', fontSize:'.55rem' }} onClick={()=>refreshGuild(activeGuildId)} disabled={loadingGuild === activeGuildId} title={t('refresh') || 'Refresh'}>
                    {t('refresh') || 'Refresh'}
                  </button>
                )}
              </div>
              <div style={{ fontSize:'.6rem', opacity:.8 }}>{totalSelected}/{MAX_CHANNELS} {t('selected') || 'selected'}</div>
            </div>
            <div style={{ flex:1, overflowY:'auto', border:'1px solid var(--border-color)', background:'#fff', padding:'.5rem', borderRadius:6, display:'flex', flexDirection:'column', gap:'.25rem', position:'relative' }}>
              {loadingGuild === activeGuildId && (
                <div style={{ fontSize:'.65rem', opacity:.7 }}>{t('loading') || 'Loading...'}</div>
              )}
              {guildAccessIssues[activeGuildId] === '403' && !loadingGuild && (
                <div style={{ margin:'auto', textAlign:'center', padding:'1rem', maxWidth:260 }}>
                  <div style={{ fontSize:'.7rem', fontWeight:600, marginBottom:'.4rem' }}>{t('noAccessServer') || 'Bot cannot access this server.'}</div>
                  <InviteBotBlock t={t} />
                </div>
              )}
              {guildAccessIssues[activeGuildId] === 'empty' && !loadingGuild && (
                <div style={{ margin:'auto', textAlign:'center', padding:'1rem', maxWidth:260 }}>
                  <div style={{ fontSize:'.65rem', marginBottom:'.35rem' }}>{t('emptyServerNoChannels') || 'No readable text channels found.'}</div>
                  <InviteBotBlock t={t} subtle />
                </div>
              )}
              {errorGuild && !currentChannels.length && !loadingGuild && !guildAccessIssues[activeGuildId] && (
                <div style={{ fontSize:'.65rem', color:'#dc2626' }}>Erreur: {errorGuild}</div>
              )}
              {!loadingGuild && !currentChannels.length && !errorGuild && !guildAccessIssues[activeGuildId] && (
                <div style={{ fontSize:'.6rem', opacity:.6 }}>{t('noChannels') || 'No channels'}</div>
              )}
              {currentChannels.map(ch => {
                const selected = auth.channelSelections?.get(activeGuildId)?.has(ch.id);
                return (
                  <button
                    key={ch.id}
                    type="button"
                    onClick={() => toggleChannel(activeGuildId, ch.id)}
                    disabled={!selected && totalSelected >= MAX_CHANNELS}
                    style={{
                      textAlign:'left',
                      display:'flex',
                      alignItems:'center',
                      gap:'.5rem',
                      width:'100%',
                      padding:'.5rem .7rem',
                      marginBottom:'.35rem',
                      fontSize:'.72rem',
                      border:'2px solid ' + (selected ? 'var(--color-primary)' : 'var(--border-color)'),
                      background: selected ? 'rgba(86,99,247,0.12)' : '#fff',
                      color:'#2f3441',
                      cursor: (selected || totalSelected < MAX_CHANNELS) ? 'pointer' : 'not-allowed',
                      fontWeight: selected ? 700 : 500,
                      opacity: (!selected && totalSelected >= MAX_CHANNELS) ? 0.45 : 1,
                    }}
                  >
                    <span style={{ width:18, height:18, flex:'0 0 auto', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#fff', border:'2px solid ' + (selected ? 'var(--color-primary)' : '#cbd0d9'), background: selected ? 'var(--color-primary)' : '#fff' }}>{selected ? '✓' : ''}</span>
                    <span style={{ opacity:.6 }}>#</span>
                    <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{ch.name}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop:'.6rem', fontSize:'.55rem', opacity:.65 }}>{t('channelSelectionHint') || 'Select up to 5 channels across all servers.'}</div>
            {/* Récapitulatif des salons sélectionnés */}
            {totalSelected > 0 && (
              <div style={{ marginTop:'.75rem', display:'flex', flexDirection:'column', gap:'.4rem' }}>
                <div style={{ fontSize:'.6rem', fontWeight:600, letterSpacing:'.5px', textTransform:'uppercase', opacity:.75 }}>{t('selectedChannels') || 'Selected channels'}</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'.4rem' }}>
                  {(() => {
                    const out = [];
                    const channelSelections = auth.channelSelections || new Map();
                    for (const [gid, setIds] of channelSelections.entries()) {
                      const guild = (auth.guilds||[]).find(g=>g.id===gid);
                      const knownChannels = channelsMap[gid] || [];
                      for (const cid of setIds) {
                        const ch = knownChannels.find(c=>c.id===cid);
                        out.push({
                          guildId: gid,
                          channelId: cid,
                          guildName: guild?.name || gid,
                          channelName: ch?.name || cid
                        });
                      }
                    }
                    return out;
                  })().map(sel => (
                    <div key={sel.guildId+':'+sel.channelId} style={{ display:'flex', alignItems:'center', gap:'.35rem', background:'#fff', border:'1px solid var(--border-color)', padding:'.25rem .5rem', fontSize:'.6rem', fontWeight:500 }}>
                      <span style={{ opacity:.7 }}>{sel.guildName} / #{sel.channelName}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={t('remove') || 'Remove'}
                        onClick={() => toggleChannel(sel.guildId, sel.channelId)}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleChannel(sel.guildId, sel.channelId); } }}
                        style={{
                          cursor:'pointer', fontSize:'.75rem', lineHeight:1, padding:'0 .15rem', color:'#dc2626', fontWeight:700, userSelect:'none'
                        }}
                      >×</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )}
        {step === 2 && (
          <div style={{ display:'flex', flexDirection:'column', minHeight:420, maxHeight:'60vh', padding:'0.9rem 1rem', gap:'.75rem' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:'.7rem', fontWeight:600 }}>{t('authorsFromChannels') || 'Authors found in selected channels'}</div>
              <div style={{ fontSize:'.6rem', opacity:.75 }}>{authorSelection.size}/{MAX_AUTHORS}</div>
            </div>
            <div style={{ flex:1, overflowY:'auto', border:'1px solid var(--border-color)', background:'#fff', borderRadius:6, padding:'.6rem', display:'grid', gap:'.4rem', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))' }}>
              {loadingAuthors && <div style={{ fontSize:'.65rem', opacity:.7 }}>{t('loading') || 'Loading...'}</div>}
              {!loadingAuthors && authors.length === 0 && (
                <div style={{ fontSize:'.6rem', opacity:.7 }}>{t('noAuthorsFound') || 'No authors found.'}</div>
              )}
              {!loadingAuthors && authors.map(a => {
                const sel = authorSelection.has(a.id);
                const avatarUrl = a.avatar ? `https://cdn.discordapp.com/avatars/${a.id}/${a.avatar}.png?size=64` : `https://cdn.discordapp.com/embed/avatars/${(parseInt(a.discriminator||'0',10)%5)}.png`;
                return (
                  <div key={a.id} onClick={() => {
                    setAuthorSelection(prev => {
                      const next = new Set(prev);
                      if (next.has(a.id)) {
                        next.delete(a.id);
                      } else {
                        if (next.size >= MAX_AUTHORS) return prev;
                        next.add(a.id);
                      }
                      return next;
                    });
                  }} style={{
                    display:'flex', alignItems:'center', gap:'.45rem', padding:'.4rem .45rem', border:'1px solid ' + (sel ? 'var(--color-primary)' : 'var(--border-color)'), background: sel ? 'rgba(86,99,247,0.12)' : '#fafafa', cursor:'pointer', fontSize:'.6rem', userSelect:'none'
                  }}>
                    <img src={avatarUrl} alt='' style={{ width:28, height:28, borderRadius:'50%', objectFit:'cover' }} />
                    <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
                      <span style={{ fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{a.global_name || a.username}</span>
                      <span style={{ fontSize:'.55rem', opacity:.65 }}>{a.count} msg</span>
                    </div>
                    <input type='checkbox' readOnly checked={sel} style={{ pointerEvents:'none', margin:0 }} />
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize:'.55rem', opacity:.65 }}>{t('authorSelectionHint') || 'Select between 2 and 10 authors.'}</div>
          </div>
        )}
        <div className="import-modal-footer" style={{ justifyContent:'flex-end', padding:'0.6rem 0.9rem', borderTop:'1px solid var(--border-color)' }}>
          {scrapeError && (
            <span style={{ color:'#dc2626', fontSize:'.6rem', marginRight:'auto', alignSelf:'center' }}>{scrapeError}</span>
          )}
          {step === 2 && (
            <button className="btn" disabled={scraping} style={{ marginRight: scrapeError ? undefined : 'auto' }} onClick={()=> setStep(1)}>{t('back') || 'Back'}</button>
          )}
          {step === 1 && (
            <button className="btn" disabled={totalSelected < 1} onClick={async () => {
              // Fetch auteurs pour les salons sélectionnés
              const channelIds = [];
              for (const setIds of (auth.channelSelections?.values() || [])) {
                for (const cid of setIds) channelIds.push(cid);
              }
              if (!channelIds.length) return;
              try {
                setLoadingAuthors(true);
                setAuthors([]);
                const serverOrigin = serverUrl;
                const r = await fetch(serverOrigin.replace(/\/$/, '') + '/api/discord/channels/authors', {
                  method:'POST',
                  headers:{ 'Content-Type':'application/json' },
                  body: JSON.stringify({ channels: channelIds })
                });
                const data = await r.json().catch(()=>({}));
                if (data.ok && Array.isArray(data.authors)) {
                  setAuthors(data.authors);
                }
              } catch {/* ignore */} finally {
                setLoadingAuthors(false);
                setStep(2);
              }
            }}>{t('next') || 'Next'}</button>
          )}
          {step === 2 && (
            <button
              className="btn"
              disabled={scraping || authorSelection.size < MIN_AUTHORS || authorSelection.size > MAX_AUTHORS}
              onClick={async () => {
                const channelIds = [];
                const guildNames = [];
                for (const [gid, setIds] of (auth.channelSelections?.entries() || [])) {
                  if (!setIds || !setIds.size) continue;
                  for (const cid of setIds) channelIds.push(cid);
                  const g = (auth.guilds || []).find((x) => x.id === gid);
                  if (g) guildNames.push(g.name);
                }
                const authorIds = [...authorSelection];
                const label = `Discord ${guildNames.join(', ')} (${channelIds.length} salons)`.replace(/\s+/g, ' ').trim();
                setAuth(prev => ({ ...prev, selectedAuthorIds: new Set(authorSelection) }));
                if (!onScrape) { onClose(); return; }
                setScrapeError(null);
                setScraping(true);
                try {
                  await onScrape({ channelIds, authorIds, label });
                  onClose();
                } catch (e) {
                  setScrapeError(scrapeErrorLabel(e?.message));
                } finally {
                  setScraping(false);
                }
              }}
            >{scraping ? (t('loading') || 'Import...') : (t('importSelection') || 'Importer la selection')}</button>
          )}
        </div>
      </div>
    </div>
  );
  return createPortal(content, document.body);
}

function InviteBotBlock({ t, subtle }) {
  // Lien d'invitation du bot (fourni explicitement par l'utilisateur) – figé pour éviter toute incohérence d'env.
  const inviteUrl = 'https://discord.com/oauth2/authorize?client_id=1416593032240955484&permissions=274877958144&scope=bot%20applications.commands';
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'.4rem', alignItems:'center' }}>
      {!subtle && <div style={{ fontSize:'.6rem', opacity:.75 }}>{t('inviteBotExplain') || 'Invite the bot to grant it access and then reopen this modal.'}</div>}
      <a href={inviteUrl} target="_blank" rel="noreferrer" className="btn" style={{ fontSize:'.65rem', padding:'.35rem .7rem' }}>{t('inviteBot') || 'Invite bot'}</a>
    </div>
  );
}

// Modal de curation des prenoms censures (mode "C'est qui ?"). Prerempli avec les
// prenoms auto-detectes ; le chef ajoute/retire librement (un par ligne ou virgule).
function NamesModal({ t, detected, value, onClose, onSave }) {
  const initial = (Array.isArray(value) && value.length) ? value : (detected || []);
  const [text, setText] = useState(initial.join('\n'));
  const [fetched, setFetched] = useState(detected || []);
  const parse = (s) => [...new Set(String(s).split(/[\n,;]+/).map((x) => x.trim()).filter((x) => x.length >= 2))];
  const list = parse(text);
  // Fallback robuste : si la liste est vide a l'ouverture, on va chercher les
  // prenoms detectes directement au serveur (le roomState peut arriver tard).
  useEffect(() => {
    if ((Array.isArray(value) && value.length) || (detected && detected.length)) return;
    const code = (window.location.hash.match(/#\/room\/([^?]+)/) || [])[1];
    if (!code) return;
    let alive = true;
    fetch(`${serverUrl}/api/rooms/${code}/detected-names`)
      .then((r) => r.json())
      .then((d) => { if (alive && Array.isArray(d.names) && d.names.length) { setFetched(d.names); setText((cur) => cur.trim() ? cur : d.names.join('\n')); } })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, color: '#2f3441', textAlign: 'left' }}>
        <h3 style={{ marginTop: 0 }}>🧑 {t('whoManageNames') || 'Gérer les prénoms'}</h3>
        <div style={{ fontSize: '.78rem', opacity: .8, marginBottom: '.5rem' }}>
          {t('whoNamesHint') || 'Un prénom par ligne. Les prénoms détectés automatiquement sont pré-remplis : complète ou retire ceux qui ne conviennent pas.'}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          style={{ width: '100%', fontFamily: 'inherit', fontSize: '.85rem', padding: '.5rem', border: '2px solid #2f3441', borderRadius: 6, resize: 'vertical' }}
          placeholder={'Tomag\nSylvie\nNadia\nAniss'}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '.6rem' }}>
          <span style={{ fontSize: '.75rem', opacity: .7 }}>{list.length} {t('whoNamesCount') || 'prénoms'}</span>
          <div style={{ display: 'flex', gap: '.5rem' }}>
            {fetched && fetched.length > 0 && (
              <button type="button" className="btn" onClick={() => setText(fetched.join('\n'))}>{t('whoNamesReset') || 'Réinitialiser (détectés)'}</button>
            )}
            <button type="button" className="btn primary" onClick={() => onSave(list)}>{t('save') || 'Enregistrer'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModeAndConfigTabs({ roomParams, setRoomParams, t, onUploadCsv, onDiscordScrape, datasetReady, datasetAuthors, detectedNames, keptAuthors, onKeptAuthors, maxAuthors, datasetPeriod, sources, onRemoveSource, uploading, uploadProgress, onSelectMode, modeAccess }) {
  const [activeTab, setActiveTab] = useState('modes'); // 'modes' | 'config'
  const [sourceChooserOpen, setSourceChooserOpen] = useState(false);
  const [namesOpen, setNamesOpen] = useState(false); // modal "gerer les prenoms" (mode whoisit)
  const [authUser, setAuthUser] = useState(getAuth().user);
  useEffect(() => subscribeAuth((a) => setAuthUser(a.user)), []);
  const lastSettings = authUser && authUser.lastSettings;
  const applyLastSettings = () => {
    if (!lastSettings) return;
    setRoomParams({
      ...roomParams,
      mode: lastSettings.mode || roomParams.mode,
      rounds: lastSettings.rounds ?? roomParams.rounds,
      messagesPerRound: lastSettings.messagesPerRound ?? roomParams.messagesPerRound,
      minMessageLength: lastSettings.minMessageLength ?? roomParams.minMessageLength,
      thinkingTimeSec: lastSettings.thinkingTimeSec ?? roomParams.thinkingTimeSec,
      allowedMedia: lastSettings.allowedMedia || roomParams.allowedMedia,
      messagesAlaSuite: lastSettings.messagesAlaSuite ?? roomParams.messagesAlaSuite,
    });
  };
  // État OAuth réel
  const [discordAuth, setDiscordAuth] = useState(null); // { user, guilds, selectedGuildIds, tokenId, channelSelections: Map<guildId, Set<channelId>> }
  const [guildsModalOpen, setGuildsModalOpen] = useState(false);
  const [oauthError, setOauthError] = useState(null);
  // wrapper pour propager dans le mini store global (badge)
  // useCallback: identite stable, sinon les effets de la modal (dep sur setAuth)
  // bouclent a chaque render (refetch reseau en boucle).
  const setDiscordAuthWrapper = useCallback((val) => {
    setDiscordAuth(val);
    setGlobalDiscordAuth(val);
  }, []);
  // Modal import fichier
  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // File
  const fileInputRef = useRef(null);
  const selectMode = (id) => {
    setRoomParams({ ...roomParams, mode: id });
    // Auto bascule sur config après choix
    setActiveTab('config');
    if (onSelectMode) onSelectMode(id); // diffuse aux membres (titre + theme live)
  };

  const modeSelected = !!roomParams.mode;
  const mashupAllowed = isMashupAllowed(authUser?.discordId);
  const typology = typologyOf(authUser?.discordId);
  const isOwner = typology === 'owner';
  const canSelectMode = (id) => {
    if (isOwner) return true;
    const cfg = modeAccess?.[id];
    if (cfg) return typology === 'admin' ? !!cfg.admins : !!cfg.players;
    const m = GAME_MODES.find(x => x.id === id); // fallback avant reception de la config
    return m?.restricted ? mashupAllowed : true;
  };
  const visibleModes = GAME_MODES; // tous visibles (les non-accessibles sont "en travaux")
  const isChrono = roomParams.mode === 'chrono';
  const isDiscussion = roomParams.mode === 'discussion';
  const isWho = roomParams.mode === 'whoisit';
  const isAudioMode = roomParams.mode === 'mashup' || roomParams.mode === 'blindtest';
  const isCreation = roomParams.mode === 'studio';

  return (
    <div style={{ flexBasis: '100%', width: '100%' }}>
      <div className="card" style={{ padding: '0.75rem', position: 'relative', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.35rem' }}>
          <button
            type="button"
            onClick={() => setActiveTab('modes')}
            className={activeTab === 'modes' ? 'tab-active' : 'tab'}
            style={tabStyle(activeTab === 'modes')}
          >{t('modes')}</button>
          <button
            type="button"
            disabled={!modeSelected}
            onClick={() => modeSelected && setActiveTab('config')}
            className={activeTab === 'config' ? 'tab-active' : 'tab'}
            style={tabStyle(activeTab === 'config', !modeSelected)}
            title={!modeSelected ? t('selectModeFirst') : undefined}
          >{t('configuration')}</button>
        </div>

        {activeTab === 'modes' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {MODE_SOURCES.map(src => {
              const modesOfSrc = visibleModes.filter(m => m.source === src.id);
              if (!modesOfSrc.length) return null;
              return (
                <div key={src.id} className="mode-group">
                  <div className="mode-group-title">{src.emoji} {t(src.labelKey)}</div>
                  <div style={{ display: 'grid', gap: '0.9rem', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                    {modesOfSrc.map(m => {
                      const locked = !canSelectMode(m.id);
                      return (
                        <ModeCard key={m.id} mode={m} selected={roomParams.mode === m.id} locked={locked} onSelect={() => { if (!locked) selectMode(m.id); }} t={t} />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'config' && ((isAudioMode || roomParams.mode === 'coverDraw' || roomParams.mode === 'studio') ? (
          <MashupConfigPanel roomParams={roomParams} setRoomParams={setRoomParams} t={t} />
        ) : isCreation ? (
          <AtelierConfigPanel roomParams={roomParams} setRoomParams={setRoomParams} t={t} />
        ) : (
          <div className="config-split">
            {/* Panneau 1 : Source des messages */}
            <div className="config-panel">
              {!modeSelected && (
                <div style={{ color: 'var(--color-danger, #ff6b6b)', fontSize: '0.9rem' }}>{t('selectModeFirst')}</div>
              )}
              <h4 style={{ margin: 0, fontSize: '0.9rem', letterSpacing: '.5px', textTransform: 'uppercase', opacity: .85 }}>{t('messagesSource')}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                {/* Blocs de sources empilees */}
                {(sources || []).map((s) => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', border: '2px solid #2f3441', background: '#fff', color: '#2f3441', padding: '.4rem .6rem', boxShadow: '0 3px 0 rgba(0,0,0,0.15)' }}>
                    <span style={{ fontSize: '1.15rem' }}>{s.type === 'discord' ? '💬' : '📄'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div>
                      <div style={{ fontSize: '.65rem', opacity: .7 }}>{s.authors} auteurs, {s.totalMessages} messages</div>
                    </div>
                    <button type="button" className="btn btn-danger" style={{ padding: '.15rem .5rem' }} disabled={uploading} onClick={() => onRemoveSource && onRemoveSource(s.id)}>X</button>
                  </div>
                ))}
                {(!sources || sources.length === 0) && (
                  <div style={{ fontSize: '.72rem', opacity: .7 }}>Aucune source. Ajoute-en une avec le bouton ci-dessous.</div>
                )}
                <button
                  type="button"
                  className="btn"
                  style={{ alignSelf: 'flex-start', fontWeight: 700 }}
                  onClick={() => setSourceChooserOpen(true)}
                  disabled={uploading}
                >+ Ajouter une source</button>

                {/* Chooser: fichier ou Discord (porte dans body pour passer au-dessus de tout) */}
                {sourceChooserOpen && createPortal(
                  <div className="modal-overlay" onClick={() => setSourceChooserOpen(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                      <h3 style={{ marginTop: 0 }}>Ajouter une source</h3>
                      <div style={{ display: 'flex', gap: '.75rem', marginTop: '.5rem' }}>
                        <button
                          type="button"
                          className="btn"
                          style={{ flex: 1, padding: '1rem .5rem', fontWeight: 700, display: 'flex', flexDirection: 'column', gap: '.3rem', alignItems: 'center' }}
                          onClick={() => { setSourceChooserOpen(false); setShowImportModal(true); }}
                        ><span style={{ fontSize: '1.6rem' }}>📄</span>Fichier CSV</button>
                        <button
                          type="button"
                          className="btn"
                          style={{ flex: 1, padding: '1rem .5rem', fontWeight: 700, display: 'flex', flexDirection: 'column', gap: '.3rem', alignItems: 'center' }}
                          onClick={() => {
                            setSourceChooserOpen(false);
                            if (!discordAuth) startDiscordOAuth(setDiscordAuthWrapper, setOauthError);
                            setGuildsModalOpen(true);
                          }}
                        ><span style={{ fontSize: '1.6rem' }}>💬</span>Serveur Discord</button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
                {showImportModal && (
                  <ImportModal
                    t={t}
                    onClose={() => { setShowImportModal(false); setPendingFile(null); }}
                    onConfirm={async () => {
                      if (pendingFile) {
                        const fakeEvent = { target: { files: [pendingFile] } };
                        await onUploadCsv(fakeEvent);
                      }
                      setShowImportModal(false);
                    }}
                    pendingFile={pendingFile}
                    setPendingFile={setPendingFile}
                    fileInputRef={fileInputRef}
                    uploading={uploading}
                  />
                )}
              </div>
              <div style={{ fontSize: '0.7rem', minHeight: '1.1rem' }}>
                {!datasetReady && !uploading && t('importBeforeStart')}
              </div>
              {uploading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ flex: 1, height: 10, border: '2px solid var(--border-color)', background: '#fff' }}>
                    <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'var(--color-primary)' }} />
                  </div>
                  <div style={{ minWidth: 48, textAlign: 'right', fontSize: '0.65rem' }}>{uploadProgress}%</div>
                </div>
              )}
              {oauthError && (
                <div style={{ color: '#dc2626', fontSize: '0.65rem' }}>{oauthError}</div>
              )}
              {/* Modal de sélection des serveurs */}
              {guildsModalOpen && discordAuth && (
                <GuildsChannelsModal
                  open={guildsModalOpen}
                  onClose={() => setGuildsModalOpen(false)}
                  auth={discordAuth}
                  setAuth={setDiscordAuthWrapper}
                  onScrape={onDiscordScrape}
                  t={t}
                />
              )}
            </div>
            <div className="config-divider" aria-hidden />
            {/* Panneau 2 : Paramètres */}
            <div className="config-panel">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.5rem', flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem', letterSpacing: '.5px', textTransform: 'uppercase', opacity: .85 }}>{t('parameters')}</h4>
                {lastSettings && (
                  <button type="button" className="btn" style={{ padding: '.2rem .55rem', fontSize: '.72rem', fontWeight: 700 }} onClick={applyLastSettings} title="Applique tes derniers reglages de partie">Reimporter mes reglages</button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
                {(isDiscussion || isWho) ? (
                  <RangeSetting
                    label={`${t('roundsLabel')}: ${roomParams.rounds}`}
                    min={1} max={10} step={1} value={roomParams.rounds}
                    onChange={v => setRoomParams({ ...roomParams, rounds: v })}
                  />
                ) : (
                  <RangeSetting
                    label={`${t('roundsLabel')}: ${roomParams.rounds}`}
                    min={5} max={50} step={5} value={roomParams.rounds}
                    onChange={v => setRoomParams({ ...roomParams, rounds: v })}
                  />
                )}
                {!isChrono && !isDiscussion && !isWho && (
                  <RangeSetting
                    label={`${t('messagesPerRoundLabel')}: ${roomParams.messagesPerRound}`}
                    min={1} max={10} step={1} value={roomParams.messagesPerRound}
                    onChange={v => setRoomParams({ ...roomParams, messagesPerRound: v })}
                  />
                )}
                {!isChrono && !isDiscussion && !isWho && (
                  <RangeSetting
                    label={`${t('minMessageLengthLabel')}: ${roomParams.minMessageLength}`}
                    min={3} max={1024} step={1} value={roomParams.minMessageLength}
                    onChange={v => setRoomParams({ ...roomParams, minMessageLength: v })}
                    info="Longueur minimale d'un message (en caracteres) pour etre propose en jeu. Plus la valeur est haute, plus les messages sont longs."
                  />
                )}
                {isWho && (
                  <RangeSetting
                    label={`${t('whoTime') || 'Temps pour deviner'}: ${roomParams.whoSec}s`}
                    min={15} max={90} step={5} value={roomParams.whoSec}
                    onChange={v => setRoomParams({ ...roomParams, whoSec: v })}
                  />
                )}
                {isDiscussion && (
                  <RangeSetting
                    label={`${t('discussionWriteTime') || 'Temps d’écriture'}: ${roomParams.writeSec}s`}
                    min={15} max={120} step={5} value={roomParams.writeSec}
                    onChange={v => setRoomParams({ ...roomParams, writeSec: v })}
                  />
                )}
                {isDiscussion && (
                  <RangeSetting
                    label={`${t('discussionVoteTime') || 'Temps de vote'}: ${roomParams.voteSec}s`}
                    min={10} max={90} step={5} value={roomParams.voteSec}
                    onChange={v => setRoomParams({ ...roomParams, voteSec: v })}
                  />
                )}
                {isChrono && (
                  <RangeSetting
                    label={`${t('chronoThinkLabel')}: ${roomParams.chronoThinkSec}s`}
                    min={5} max={60} step={5} value={roomParams.chronoThinkSec}
                    onChange={v => setRoomParams({ ...roomParams, chronoThinkSec: v })}
                  />
                )}
                {!isChrono && !isDiscussion && !isWho && (
                  <RangeSetting
                    label={`${t('thinkingTimeLabel')}: ${roomParams.thinkingTimeSec}`}
                    min={3} max={20} step={1} value={roomParams.thinkingTimeSec}
                    onChange={v => setRoomParams({ ...roomParams, thinkingTimeSec: v })}
                  />
                )}
              </div>
              {isDiscussion && (
                <div style={{ fontSize: '.72rem', opacity: .75, marginTop: '.5rem' }}>{t('discussionConfigHint') || 'Chacun écrit une fausse réponse, puis doit retrouver la vraie. Trouver : +2. Piéger : +1.'}</div>
              )}
              {isWho && (
                <div style={{ marginTop: '.5rem' }}>
                  <div style={{ fontSize: '.72rem', opacity: .75 }}>{t('whoConfigHint') || 'Un prénom est censuré, les auteurs anonymisés. Une orthographe proche est acceptée.'}</div>
                  <button type="button" className="btn" style={{ marginTop: '.4rem', fontWeight: 700 }} onClick={() => setNamesOpen(true)}>
                    🧑 {t('whoManageNames') || 'Gérer les prénoms'} ({(roomParams.whoNames && roomParams.whoNames.length) || (detectedNames || []).length})
                  </button>
                </div>
              )}
              {namesOpen && createPortal(
                <NamesModal
                  t={t}
                  detected={detectedNames || []}
                  value={roomParams.whoNames}
                  onClose={() => setNamesOpen(false)}
                  onSave={(list) => { setRoomParams({ ...roomParams, whoNames: list }); setNamesOpen(false); }}
                />, document.body)}
              {!isChrono && !isWho && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Types :</span>
                  {[
                    { key: 'text', label: 'Texte' },
                    { key: 'image', label: 'Image' },
                    { key: 'gif', label: 'Gif' },
                    { key: 'sound', label: 'Son' },
                  ].map(({ key, label }) => {
                    const am = roomParams.allowedMedia || { text: true, image: true, gif: true, sound: true };
                    const active = !!am[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={active}
                        onClick={() => {
                          const next = { ...am, [key]: !active };
                          if (!next.text && !next.image && !next.gif && !next.sound) return; // au moins un type
                          setRoomParams({ ...roomParams, allowedMedia: next });
                        }}
                        style={toggleBtnStyle(active)}
                      >{active ? '✓ ' : ''}{label}</button>
                    );
                  })}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <input
                    type="checkbox"
                    checked={!!roomParams.messagesAlaSuite}
                    onChange={e => setRoomParams({ ...roomParams, messagesAlaSuite: e.target.checked })}
                  />
                  {t('messagesInARow')}
                </label>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginLeft: 'auto', maxWidth: '240px' }}>{t('configureThenStart')}</div>
              </div>
              )}
              {!isChrono && datasetPeriod && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  <span style={{ fontWeight: 600 }}>Période :</span>
                  <input
                    type="date"
                    min={msToDateInput(datasetPeriod.min)}
                    max={msToDateInput(datasetPeriod.max)}
                    value={roomParams.periodAfter ? msToDateInput(roomParams.periodAfter) : ''}
                    onChange={e => setRoomParams({ ...roomParams, periodAfter: e.target.value ? Date.parse(e.target.value) : null })}
                  />
                  <span>à</span>
                  <input
                    type="date"
                    min={msToDateInput(datasetPeriod.min)}
                    max={msToDateInput(datasetPeriod.max)}
                    value={roomParams.periodBefore ? msToDateInput(roomParams.periodBefore) : ''}
                    onChange={e => setRoomParams({ ...roomParams, periodBefore: e.target.value ? (Date.parse(e.target.value) + 86399999) : null })}
                  />
                  {(roomParams.periodAfter || roomParams.periodBefore) && (
                    <button type="button" className="btn" style={{ padding: '0.2rem 0.5rem' }} onClick={() => setRoomParams({ ...roomParams, periodAfter: null, periodBefore: null })}>Tout</button>
                  )}
                </div>
              )}
              {datasetReady && (
                <AuthorCurationPanel authors={datasetAuthors} kept={keptAuthors} maxAuthors={maxAuthors} onChange={onKeptAuthors} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const MASHUP_MAX_AUTHORS = 20;

// Configuration du mode "mashup" : duree d'extrait, nombre de manches,
// periodes d'extraction (multi), et selection des auteurs (liste depuis Terrashup).
function MashupConfigPanel({ roomParams, setRoomParams, t }) {
  const [authors, setAuthors] = useState(null); // [{ label, name, count }] | null (loading)
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setErr(false);
    fetch(`${serverUrl}/api/mashup/authors`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('http'))))
      .then(d => {
        if (!alive) return;
        const list = (d.authors || []).map(a => ({ label: a.name, name: a.name, count: a.mashups }));
        setAuthors(list);
        // Defaut : tous les auteurs selectionnes (dans la limite max).
        setRoomParams(rp => {
          if (rp.allowedMashupAuthors && rp.allowedMashupAuthors.length) return rp;
          return { ...rp, allowedMashupAuthors: list.slice(0, MASHUP_MAX_AUTHORS).map(a => a.label) };
        });
      })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [setRoomParams]);

  const em = roomParams.extractionModes || { start: true, middle: true, random: true };
  const toggleEm = (k) => {
    const next = { ...em, [k]: !em[k] };
    if (!next.start && !next.middle && !next.random) return; // au moins un
    setRoomParams({ ...roomParams, extractionModes: next });
  };

  const periods = [
    { key: 'start', label: t('extractionStart') },
    { key: 'middle', label: t('extractionMiddle') },
    { key: 'random', label: t('extractionRandom') },
  ];

  const isCoverDraw = roomParams.mode === 'coverDraw';
  const isStudio = roomParams.mode === 'studio';
  return (
    <div className="config-panel" style={{ width: '100%' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', letterSpacing: '.5px', textTransform: 'uppercase', opacity: .85 }}>{t('parameters')}</h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
        {isCoverDraw ? (
          <RangeSetting
            label={`${t('coverDrawTime') || 'Temps de dessin'}: ${Math.round((roomParams.coverDrawSec || 240) / 60)} min`}
            min={60} max={600} step={30} value={roomParams.coverDrawSec || 240}
            onChange={v => setRoomParams({ ...roomParams, coverDrawSec: v })}
          />
        ) : isStudio ? (
          <>
            <RangeSetting
              label={`${t('studioLoopLabel') || 'Longueur de boucle'}: ${roomParams.studioLoopSec || 15}s`}
              min={4} max={30} step={1} value={roomParams.studioLoopSec || 15}
              onChange={v => setRoomParams({ ...roomParams, studioLoopSec: v })}
            />
            <RangeSetting
              label={`${t('studioCreateLabel') || 'Temps de création'}: ${Math.round((roomParams.createSec || 180) / 60)} min`}
              min={60} max={600} step={30} value={roomParams.createSec || 180}
              onChange={v => setRoomParams({ ...roomParams, createSec: v })}
            />
            <RangeSetting
              label={`${t('studioRateLabel') || 'Temps de notation'}: ${roomParams.rateSec || 45}s`}
              min={15} max={120} step={5} value={roomParams.rateSec || 45}
              onChange={v => setRoomParams({ ...roomParams, rateSec: v })}
            />
            <RangeSetting
              label={`${t('roundsLabel')}: ${roomParams.rounds}`}
              min={1} max={5} step={1} value={roomParams.rounds}
              onChange={v => setRoomParams({ ...roomParams, rounds: v })}
            />
          </>
        ) : (
          <RangeSetting
            label={`${t('clipDurationLabel')}: ${roomParams.clipSeconds}s`}
            min={5} max={60} step={1} value={roomParams.clipSeconds}
            onChange={v => setRoomParams({ ...roomParams, clipSeconds: v })}
          />
        )}
        {!isCoverDraw && !isStudio && (
          <RangeSetting
            label={`${t('roundsLabel')}: ${roomParams.rounds}`}
            min={5} max={50} step={5} value={roomParams.rounds}
            onChange={v => setRoomParams({ ...roomParams, rounds: v })}
          />
        )}
        {roomParams.mode === 'blindtest' && (
          <RangeSetting
            label={`${t('blindtestAttemptsLabel')}: ${roomParams.blindtestAttempts}`}
            min={1} max={5} step={1} value={roomParams.blindtestAttempts}
            onChange={v => setRoomParams({ ...roomParams, blindtestAttempts: v })}
          />
        )}
      </div>
      {isStudio && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '.25rem', fontSize: '0.85rem', fontWeight: 600 }}>
          <input type="checkbox" checked={!!roomParams.studioSameMashup} onChange={e => setRoomParams({ ...roomParams, studioSameMashup: e.target.checked })} />
          🎯 {t('studioSameMashup') || 'Le même mashup pour tout le monde'}
        </label>
      )}
      {roomParams.mode === 'mashup' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '.25rem', fontSize: '0.85rem', fontWeight: 600 }}>
          <input type="checkbox" checked={!!roomParams.drawEnabled} onChange={e => setRoomParams({ ...roomParams, drawEnabled: e.target.checked })} />
          🎨 {t('drawEnableLabel') || 'Zone de dessin partagée'}
        </label>
      )}
      <div style={{ display: (isCoverDraw || isStudio) ? 'none' : 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginTop: '.25rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{t('extractionPeriodLabel')} :</span>
        {periods.map(({ key, label }) => {
          const active = !!em[key];
          return (
            <button
              key={key}
              type="button"
              aria-pressed={active}
              onClick={() => toggleEm(key)}
              style={toggleBtnStyle(active)}
            >{active ? '✓ ' : ''}{label}</button>
          );
        })}
      </div>
      <div style={{ marginTop: '.5rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{t('allowedAuthorsLabel')}</span>
        {authors === null && !err && <div style={{ fontSize: '.75rem', opacity: .7 }}>…</div>}
        {err && <div style={{ color: '#dc2626', fontSize: '.75rem' }}>{t('mashupLoadError')}</div>}
        {authors && (
          <AuthorCurationPanel
            authors={authors}
            kept={roomParams.allowedMashupAuthors || []}
            maxAuthors={MASHUP_MAX_AUTHORS}
            onChange={labels => setRoomParams({ ...roomParams, allowedMashupAuthors: labels })}
          />
        )}
      </div>
    </div>
  );
}

function AtelierConfigPanel({ roomParams, setRoomParams, t }) {
  return (
    <div className="config-panel" style={{ width: '100%' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', letterSpacing: '.5px', textTransform: 'uppercase', opacity: .85 }}>{t('parameters')}</h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
        <RangeSetting label={`${t('roundsLabel')}: ${roomParams.rounds}`} min={1} max={10} step={1} value={roomParams.rounds} onChange={v => setRoomParams({ ...roomParams, rounds: v })} />
        <RangeSetting label={`${t('studioCreateTime') || 'Temps de studio'}: ${roomParams.createSec}s`} min={20} max={180} step={10} value={roomParams.createSec} onChange={v => setRoomParams({ ...roomParams, createSec: v })} />
        <RangeSetting label={`${t('atelierRateTime') || 'Temps de notation'}: ${roomParams.rateSec}s`} min={15} max={90} step={5} value={roomParams.rateSec} onChange={v => setRoomParams({ ...roomParams, rateSec: v })} />
      </div>
      <div style={{ fontSize: '.72rem', opacity: .75, marginTop: '.5rem' }}>{t('studioConfigHint') || 'Chacun compose une prod (beat + voix), puis tout le monde note à l’aveugle. Reveal du dernier au premier.'}</div>
    </div>
  );
}

function ModeCard({ mode, selected, onSelect, locked, t }) {
  return (
    <div
      role="button"
      tabIndex={locked ? -1 : 0}
      aria-disabled={locked || undefined}
      onClick={() => { if (!locked) onSelect(); }}
      onKeyDown={e => { if (!locked && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelect(); } }}
      className={`mode-card${selected ? ' selected' : ''}${locked ? ' locked' : ''}`}
    >
      <div className="mode-emoji" aria-hidden>{mode.emoji}</div>
      <div className="mode-title">{t(mode.nameKey)}</div>
      <div className="mode-desc">{t(mode.descKey)}</div>
      {locked && (
        <div className="mode-tape" aria-hidden>
          {/* Rubalise "en travaux" : bandes edge-to-edge dans tous les sens (encerclement) */}
          <span className="tape t1" /><span className="tape t2" /><span className="tape t3" />
          <span className="tape t4" /><span className="tape t5" />
          <span className="mode-tape-label">EN TRAVAUX</span>
        </div>
      )}
    </div>
  );
}

function RangeSetting({ label, min, max, step, value, onChange, info }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <span>{label}</span>
        {info && (
          <span
            title={info}
            aria-label={info}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 16, height: 16, borderRadius: '50%',
              border: '1px solid var(--color-primary)', color: 'var(--color-primary)',
              fontSize: 11, fontWeight: 700, cursor: 'help', userSelect: 'none', lineHeight: 1,
            }}
          >i</span>
        )}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(parseInt(e.target.value, 10))}
      />
    </label>
  );
}

function msToDateInput(ms) {
  try { return new Date(ms).toISOString().slice(0, 10); } catch { return ''; }
}

// Style clair pour les boutons toggle (selectionne = rempli + ombre + gras ;
// non selectionne = grise + bordure pointillee -> etat lisible d'un coup d'oeil).
function toggleBtnStyle(active) {
  return {
    padding: '0.3rem 0.8rem', borderRadius: 0, cursor: 'pointer', fontSize: '0.8rem',
    fontWeight: active ? 800 : 600,
    border: active ? '2px solid var(--color-primary)' : '2px dashed #c7ccd8',
    background: active ? 'var(--color-primary)' : '#eef0f6',
    color: active ? '#fff' : '#8a90a2',
    boxShadow: active ? '0 3px 0 var(--color-secondary)' : 'none',
    transition: 'background .15s, color .15s, box-shadow .15s',
  };
}

function tabStyle(active, disabled) {
  return {
    position: 'relative',
    padding: '0.5rem 1rem',
    border: 'none',
    background: 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: active ? 700 : 500,
    color: disabled ? 'var(--text-secondary)' : 'var(--color-text)',
    borderBottom: active ? '3px solid var(--color-primary)' : '3px solid transparent',
    opacity: disabled ? 0.5 : 1,
    transition: 'color .2s, opacity .2s'
  };
}

function ImportModal({ t, onClose, onConfirm, pendingFile, setPendingFile, fileInputRef, uploading }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.classList.add('import-modal-open');
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.classList.remove('import-modal-open');
    };
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const modalContent = (
    <div ref={overlayRef} className="import-modal-overlay" onMouseDown={handleOverlayClick}>
      <div className="import-modal card" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="import-modal-header" style={{ justifyContent: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{t('importFromFile')}</h3>
        </div>
        <div className="import-modal-body">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setPendingFile(f);
            }}
          />
          {!pendingFile && (
            <div className="import-drop" onClick={() => fileInputRef.current?.click()}>
              <div style={{ fontSize: '0.8rem' }}>{t('dragOrClick')}</div>
              <div style={{ fontSize: '0.65rem', opacity: .6, marginTop: '.25rem' }}>.csv</div>
            </div>
          )}
          {pendingFile && (
            <div className="import-file-details">
              <div className="file-row">
                <strong>{pendingFile.name}</strong>
                <span style={{ fontSize: '0.65rem', opacity: .6 }}>{(pendingFile.size/1024).toFixed(1)} KB</span>
              </div>
            </div>
          )}
          {uploading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1, height: 10, border: '2px solid var(--border-color)', background: '#fff' }}>
                <div className="upload-progress-bar" />
              </div>
              <div style={{ fontSize: '0.65rem' }}>{t('connecting')}</div>
            </div>
          )}
          {/* Ne pas afficher d'état importOk dans la modal; il est visible dans la zone principale */}
        </div>
        <div className="import-modal-footer" style={{ justifyContent: 'center', gap: '0.9rem' }}>
          <button
            className="btn"
            disabled={!pendingFile}
            onClick={onConfirm}
          >{t('confirm')}</button>
          <button
            className="btn"
            onClick={onClose}
          >{t('cancel')}</button>
        </div>
      </div>
    </div>
  );

  // Portal pour éviter le bug : un ancêtre avec transform (ex: .card:hover) change le conteneur de position:fixed.
  return createPortal(modalContent, document.body);
}
